import React from 'react';
import { Box, CircularProgress, Stack, TextField, Typography, Autocomplete, Card, CardContent, Chip, Grid, Button, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { listExams } from '../services/indianExamsService';
import { useNavigate } from 'react-router-dom';
import ExamsGrid from '../components/ExamsGrid';
import PageLayoutWithAds from '../components/layout/PageLayoutWithAds';

const UI_TOKENS = {
  heroGradient: 'radial-gradient(circle at 70% 30%, rgba(10, 126, 164, 0.08) 0%, rgba(255, 255, 255, 0) 52%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
  heroChipBg: 'rgba(10, 126, 164, 0.10)',
  heroChipColor: '#0a7ea4',
  radius: 0,
  accent: '#0a7ea4',
  primaryButton: {
    borderRadius: 8,
    fontWeight: 800,
    textTransform: 'none',
    px: 3
  },
};

function normalizeExam(x) {
  // Flatten topics for search
  const topics = [];
  try {
    (x.examStages || []).forEach(s => {
      (s.subjects || []).forEach(sub => {
        if (Array.isArray(sub.topics)) topics.push(...sub.topics);
      });
    });
  } catch { }
  return {
    id: x.id,
    name: x.name || x.shortName || x.id,
    shortName: x.shortName || x.name || x.id,
    categoryId: x.categoryId || x.type || 'other',
    categoryName: x.categoryName || x.type || 'Other',
    examYear: x.examYear || '',
    posts: Array.isArray(x.posts) ? x.posts : [],
    difficulty: x.difficulty || '',
    duration: x.duration || '',
    stages: Array.isArray(x.examStages) ? x.examStages.length : 0,
    topics,
  };
}

const labelizeCategory = (c) => ({ bank: 'Bank', police: 'Police', railway: 'Railway', ssc: 'SSC' }[String(c).toLowerCase()] || (c || 'Other'));

export default function ExamsCatalog({ embedded = false, showHeader = true } = {}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [exams, setExams] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [category, setCategory] = React.useState('all');
  const [year, setYear] = React.useState('');
  const [selectedExam, setSelectedExam] = React.useState(null);
  const navigate = useNavigate();

  const load = React.useCallback(async (page = 1, limit = 20) => {
    setLoading(true); setError('');
    try {
      const { exams } = await listExams(page, limit);
      setExams(exams.map(normalizeExam));
    } catch (e) {
      setError(e?.message || 'Failed to load exams');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const categories = React.useMemo(() => {
    const set = new Set(exams.map(e => String(e.categoryId || 'other').toLowerCase()));
    const arr = Array.from(set);
    arr.sort();
    return ['all', ...arr];
  }, [exams]);

  const categoryOptions = React.useMemo(() => (
    categories.map(c => ({ id: c, label: c === 'all' ? 'All categories' : labelizeCategory(c) }))
  ), [categories]);

  const yearOptions = React.useMemo(() => {
    const set = new Set(
      exams.map(e => e.examYear).filter(Boolean).map(String)
    );
    const list = Array.from(set).sort((a, b) => Number(b) - Number(a));
    return [{ label: 'All years', value: '' }, ...list.map(y => ({ label: y, value: y }))];
  }, [exams]);


  // Compute filtered exams based on search, category, year
  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = exams;
    if (category !== 'all') {
      list = list.filter(e => String(e.categoryId || 'other').toLowerCase() === category);
    }
    if (year) {
      list = list.filter(e => String(e.examYear || '') === String(year));
    }
    if (!term) return list;
    return list.filter(e => [e.name, e.shortName, e.categoryName, e.categoryId, e.examYear, e.posts?.join(', '), (e.topics || []).join(', ')].join(' ').toLowerCase().includes(term));
  }, [exams, q, category, year]);

  // If an exam is selected, show all exams with the same label as the selected exam
  const gridExams = React.useMemo(() => {
    if (selectedExam && selectedExam.label) {
      // Show all exams with the same label (name or shortName)
      return filtered.filter(e => (e.name || e.shortName || e.id) === selectedExam.label);
    }
    return filtered;
  }, [filtered, selectedExam]);

  const examOptions = React.useMemo(() => (
    filtered.map(e => ({ id: e.id, label: e.name || e.shortName || e.id, group: labelizeCategory(String(e.categoryId || 'other').toLowerCase()) }))
  ), [filtered]);

  const heroStats = React.useMemo(() => {
    const totalExams = exams.length;
    const totalCategories = Math.max(0, categories.length - 1);
    const withTopics = exams.filter((e) => Array.isArray(e.topics) && e.topics.length > 0).length;
    return { totalExams, totalCategories, withTopics };
  }, [exams, categories]);

  const content = (
    <>
      {showHeader ? (
        <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>Explore Exams</Typography>
      ) : null}

      <Box
        sx={{
          mb: 4,
          p: 6,
          borderRadius: '16px',
          background: UI_TOKENS.heroGradient,
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: UI_TOKENS.accent, 
                fontWeight: 800, 
                letterSpacing: 2,
                mb: 1.5,
                display: 'block'
              }}
            >
              Elite Exam Catalog
            </Typography>
            <Typography variant="h3" fontWeight={900} sx={{ mb: 2, letterSpacing: '-0.02em', color: 'primary.dark' }}>
              Explore. <Box component="span" sx={{ color: UI_TOKENS.accent }}>Prepare.</Box> Conquer.
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8, fontWeight: 500, maxWidth: 600, fontSize: '1.1rem' }}>
              Choose from over {heroStats.totalExams} comprehensive exam paths curated by AI and experts to accelerate your success.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Chip 
                label={`${heroStats.totalExams} Exams`} 
                sx={{ bgcolor: UI_TOKENS.heroChipBg, color: UI_TOKENS.heroChipColor, fontWeight: 700, borderRadius: '16px', border: '1px solid', borderColor: 'rgba(45, 91, 255, 0.2)' }} 
              />
              <Chip 
                label={`${heroStats.totalCategories} Categories`} 
                sx={{ bgcolor: UI_TOKENS.heroChipBg, color: UI_TOKENS.heroChipColor, fontWeight: 700, borderRadius: '16px', border: '1px solid', borderColor: 'rgba(45, 91, 255, 0.2)' }} 
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: '16px', 
            border: '1px solid', 
            borderColor: 'divider', 
            boxShadow: 'none',
            background: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip size="small" label="PRACTICE" sx={{ fontWeight: 800, bgcolor: 'rgba(45, 91, 255, 0.08)', color: UI_TOKENS.accent, borderRadius: '16px' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>AI-POWERED</Typography>
                </Box>
                <Typography variant="h5" fontWeight={900} color="primary.dark">Descriptive Practice</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Master mains-style writing with AI-guided practice and real-time structure feedback.
                </Typography>
                <Box pt={1}>
                  <Button variant="contained" sx={UI_TOKENS.primaryButton} onClick={() => navigate('/subject-practice')}>
                    Start Practice
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: '16px', 
            border: '1px solid', 
            borderColor: 'divider', 
            boxShadow: 'none',
            background: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip size="small" label="WRITING" sx={{ fontWeight: 800, bgcolor: 'rgba(76, 175, 80, 0.08)', color: '#2E7D32', borderRadius: '16px' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>GENERATIVE AI</Typography>
                </Box>
                <Typography variant="h5" fontWeight={900} color="primary.dark">Essay Improvement</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Build clarity and argument quality through targeted, AI-assisted writing labs.
                </Typography>
                <Box pt={1}>
                  <Button variant="contained" color="success" sx={{ ...UI_TOKENS.primaryButton, bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }} onClick={() => navigate('/genai-papers')}>
                    Improve Writing
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" fontWeight={900} color="primary.dark" sx={{ mb: 2.5, letterSpacing: '-0.01em' }}>
        Curated Exam Catalog
      </Typography>

      {/* Filters row: dropdowns and search in the same row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 4 }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" sx={{ mb: { xs: 1, md: 0 } }}>
          <Autocomplete
            size="medium"
            options={categoryOptions}
            getOptionLabel={(o) => o.label}
            value={categoryOptions.find(o => o.id === category) || null}
            onChange={(_, val) => setCategory(val?.id || 'all')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Category" sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />}
          />

          <Autocomplete
            size="medium"
            options={yearOptions}
            getOptionLabel={(o) => o.label}
            value={yearOptions.find(o => o.value === String(year)) || yearOptions[0]}
            onChange={(_, val) => setYear(val?.value || '')}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={(params) => <TextField {...params} label="Year" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />}
          />

          <Autocomplete
            size="medium"
            options={examOptions}
            groupBy={category === 'all' ? (o) => o.group : undefined}
            getOptionLabel={(o) => o.label}
            value={selectedExam}
            onChange={(_, val) => { setSelectedExam(val); if (val?.id) navigate(`/papers?examId=${encodeURIComponent(val.id)}`); }}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            renderInput={(params) => <TextField {...params} label="Specific Exam" sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />}
          />
        </Stack>

        <TextField
          size="medium"
          placeholder="Quick search..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ 
            maxWidth: 300, 
            minWidth: { xs: '100%', md: 240 },
            '& .MuiOutlinedInput-root': { borderRadius: '16px' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>


      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading exams…</Typography>
        </Stack>
      ) : null}

      {error ? (
        <Box sx={{ color: 'error.main' }}>{error}</Box>
      ) : null}

      <ExamsGrid exams={gridExams} />
    </>
  );

  if (embedded) {
    return content;
  }
  return (
    <PageLayoutWithAds 
      showTopBanner={true}
      showSidebar={true}
      showBottomSticky={true}
      maxWidth="xl"
    >
      {content}
    </PageLayoutWithAds>
  );
}
